import { axios } from "@via/api/axios.tsx";
import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import { ErrorNotification } from "@via/utils/interfaces";
import { useNotifications } from "@via/hooks/useNotifications";
import { AxiosResponse } from "axios";
import {
  AddFilesVmetaDataSetRequestBody,
  AddExistingFilesVmetaDataSetRequestBody,
  CreateVmetaDataSetRequestBody,
  UpdateVmetaDataSetRequestBody,
  VmetaData,
  VmetaDataListParams,
  VmetaDataRequestParams,
  VmetaDataResponseBody,
  VmetaDataSetFileListResponseBody,
  VmetaDataSetRequestParams,
  VmetaDataSetResponseBody,
  VmetaDataSetsResponseBody,
  VmetaDownloadUrlsResponseBody,
  VmetaDownloadUrlsRequestBody,
  RemoveFilesFromVmetaDataSetBody,
} from "common";
import { createMongoFilter, getErrorResponseMessage } from "@via/utils/helpers";
import { FilterItem } from "@via/pages/data/premium-dataset/utils/helper";
import {
  fetchVmetaCollections,
  fetchVmetaFields,
} from "@via/pages/projects/metadata/metadata.api";
import { type FileFieldNameLabel } from "@via/pages/data/file-organizer/helpers.ts";
import { PremiumDatasetViewType } from "@via/pages/data/data-collections/components/data-collection-filter";
import { ROUTES } from "@via/utils/constants";
import { useNavigate } from "react-router-dom";

export const fetchPremiumDatasetList = async (
  take: number,
  skip: number,
  sort: Array<{ id: string; desc: boolean }>,
  filter?: Array<{ id: string; value: unknown }>,
  premiumDatasetViewType?: PremiumDatasetViewType,
  mongoUserId?: string,
  tagIds: string[] = []
): Promise<VmetaDataSetsResponseBody> => {
  try {
    const baseUrl = "/api/v1/vmeta/dataset/search";
    const payload: any = {
      take,
      skip,
    };

    if (tagIds.length > 0) {
      payload.tagIds = tagIds;
    }

    if (sort.length > 0) {
      payload.sort = sort[0].id;
      payload.order = sort[0].desc ? "desc" : "asc";
    }

    const mongoFilter: Record<string, any> = {
      ...((filter && filter.length > 0
        ? createMongoFilter(filter, {
            name: "regex",
            createdAt: "gte,lte",
            updatedAt: "gte,lte",
            "owner.name": "regex",
          })
        : {}) || {}),
    };

    if (premiumDatasetViewType && mongoUserId) {
      switch (premiumDatasetViewType) {
        case PremiumDatasetViewType.My:
          mongoFilter["owner._id"] = { eq: mongoUserId };
          break;
        case PremiumDatasetViewType.Shared:
          mongoFilter["owner._id"] = { ne: mongoUserId };
          mongoFilter["perms.read.user"] = { nin: "everyone" };
          break;
        case PremiumDatasetViewType.Public:
          mongoFilter["perms.read.user"] = { in: "everyone" };
          break;
      }
    }

    if (mongoFilter && Object.keys(mongoFilter).length > 0) {
      payload.filter = mongoFilter;
    }

    const res = await axios.post(baseUrl, payload);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const usePremiumDatasetList = (
  page: number = 0,
  pageSize: number = 10,
  sort: Array<{ id: string; desc: boolean }> = [],
  filter: Array<{ id: string; value: unknown }> = [],
  enabled: boolean = true,
  premiumDatasetViewType?: PremiumDatasetViewType,
  mongoUserId?: string,
  tagIds: string[] = []
): UseQueryResult<VmetaDataSetsResponseBody, Error> => {
  const skip = page * pageSize;
  const take = pageSize;

  return useQuery<VmetaDataSetsResponseBody, Error>({
    queryKey: [
      "/api/v1/vmeta/dataset/search",
      take,
      skip,
      sort,
      filter,
      premiumDatasetViewType,
      mongoUserId,
      tagIds,
    ],
    queryFn: async () =>
      await fetchPremiumDatasetList(
        take,
        skip,
        sort,
        filter,
        premiumDatasetViewType,
        mongoUserId,
        tagIds
      ),
    retry: false,
    enabled,
  });
};

export const useDeleteDataset = (): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async (id: { id: string }) => {
      const res = await axios.delete(`/api/v1/vmeta/dataset/${id}`);
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "premium-dataset-successfully-deleted",
        message: "Dataset Successfully Deleted",
        details: "This action cannot be undone",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/v1/vmeta/dataset/search"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "premium-dataset-deletion-failed",
        message: "Dataset Deletion Failed",
        details:
          error?.response?.data?.message ||
          "Contact support if the issue persists",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const fetchPremiumDatasetFileList = async (
  id: string,
  take: number,
  skip: number,
  sort: Array<{ id: string; desc: boolean }>,
  filter?: { or: FilterItem[] }
): Promise<VmetaDataSetFileListResponseBody> => {
  try {
    const baseUrl = `/api/v1/vmeta/dataset/${id}/files/search`;
    const payload: any = {
      take,
      skip,
    };

    if (sort.length > 0) {
      payload.sort = sort[0].id;
      payload.order = sort[0].desc ? "desc" : "asc";
    }

    if (filter && "or" in filter && filter.or.length > 0) {
      const mongoFilter = createMongoFilter(filter, {});

      payload.filter = mongoFilter;
    }

    const res = await axios.post(baseUrl, payload);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const usePremiumDatasetFileList = (
  id,
  page: number = 0,
  pageSize: number = 10,
  sort: Array<{ id: string; desc: boolean }> = [],
  filter: { or: FilterItem[] } = { or: [] }
): UseQueryResult<VmetaDataSetFileListResponseBody, Error> => {
  const skip = page * pageSize;
  const take = pageSize;

  return useQuery<VmetaDataSetFileListResponseBody, Error>({
    queryKey: [
      "premiumDatasetFileList",
      `/api/v1/vmeta/dataset/${id}/files/search`,
      take,
      skip,
      sort,
      filter,
    ],
    queryFn: async () =>
      await fetchPremiumDatasetFileList(id, take, skip, sort, filter),
    retry: false,
  });
};

interface FetchAllDatasetFilesParams {
  datasetId: string;
  sort?: Array<{ id: string; desc: boolean }>;
  filter?: { or: FilterItem[] };
}

/**
 * Mutation hook for fetching all dataset files imperatively.
 * Reuses fetchPremiumDatasetFileList internally.
 * Use this when you need to fetch all files on-demand (e.g., for downloads).
 */
export const usePremiumDatasetFileListAll = (): UseMutationResult<
  VmetaDataSetFileListResponseBody,
  ErrorNotification,
  FetchAllDatasetFilesParams
> => {
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<
    VmetaDataSetFileListResponseBody,
    ErrorNotification,
    FetchAllDatasetFilesParams
  >({
    mutationFn: async ({ datasetId, sort = [], filter }) =>
      await fetchPremiumDatasetFileList(datasetId, 10000, 0, sort, filter),
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "fetch-all-dataset-files-failed",
        message: "Failed to Fetch Dataset Files",
        details:
          error?.response?.data?.message ??
          "We couldn't fetch the dataset files.",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useCreatePremiumDataset = (): UseMutationResult<
  VmetaDataSetResponseBody,
  ErrorNotification,
  CreateVmetaDataSetRequestBody
> => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<
    VmetaDataSetResponseBody,
    ErrorNotification,
    CreateVmetaDataSetRequestBody
  >({
    mutationFn: async (
      payload: CreateVmetaDataSetRequestBody
    ): Promise<VmetaDataSetResponseBody> => {
      const res = await axios.post(`/api/v1/vmeta/dataset/create`, payload);
      return res.data;
    },
    onSuccess: (response: VmetaDataSetResponseBody): void => {
      setNotification({
        id: "premium-dataset-successfully-created",
        message: "Dataset Successfully Created",
        details: "You can now add files to your dataset",
        variant: NOTIFICATION.SUCCESS,
      });
      navigate(`${ROUTES.PREMIUM_COLLECTIONS}/${response.data._id}`);
      queryClient.invalidateQueries({
        queryKey: ["/api/v1/vmeta/dataset/search"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "premium-dataset-creation-failed",
        message: "Dataset Creation Failed",
        details:
          getErrorResponseMessage(error) ??
          "We were unable to upload your file",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useEditPremiumDataset = (): UseMutationResult<
  VmetaDataSetResponseBody,
  ErrorNotification,
  VmetaDataSetRequestParams & UpdateVmetaDataSetRequestBody
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<
    VmetaDataSetResponseBody,
    ErrorNotification,
    VmetaDataSetRequestParams & UpdateVmetaDataSetRequestBody
  >({
    mutationFn: async (
      payload: VmetaDataSetRequestParams & UpdateVmetaDataSetRequestBody
    ) => {
      const body = {
        name: payload.name,
        perms: payload.perms,
      };
      const res = await axios.patch(
        `/api/v1/vmeta/dataset/${payload.id}`,
        body
      );
      return res.data;
    },
    onSuccess: (_response, variables): void => {
      setNotification({
        id: "premium-dataset-successfully-edited",
        message: "Dataset Successfully Updated",
        details: "You can now add files to your dataset",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/v1/vmeta/dataset/search"],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/v1/vmeta/dataset", variables.id],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "premium-dataset-update-failed",
        message: "Dataset Update Failed",
        details:
          getErrorResponseMessage(error) ??
          "We were unable to update your dataset",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useDeleteDatasetFile = (): UseMutationResult<
  VmetaDataSetResponseBody,
  ErrorNotification,
  RemoveFilesFromVmetaDataSetBody
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<
    VmetaDataSetResponseBody,
    ErrorNotification,
    RemoveFilesFromVmetaDataSetBody
  >({
    mutationFn: async ({ datasetId, canvasId, fileIds }) => {
      const res = await axios.post(
        `/api/v1/vmeta/dataset/${datasetId}/removeFiles`,
        { canvasId, fileIds }
      );
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "premium-dataset-file-successfully-removed",
        message: "Files Removed From Dataset",
        details: "",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({ queryKey: ["premiumDatasetFileList"] });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "premium-dataset-file-remove-failed",
        message: "Failed to Remove Files From Dataset",
        details:
          error?.response?.data?.message ||
          "Contact support if the issue persists",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useEditDatasetFile = (): UseMutationResult<
  VmetaDataResponseBody,
  ErrorNotification,
  VmetaData
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<VmetaDataResponseBody, ErrorNotification, VmetaData>({
    mutationFn: async (payload: VmetaData) => {
      const {
        _id,
        owner,
        lastUpdatedUser,
        updatedAt,
        createdAt,
        canvasId,
        canvasFileId,
        ...body
      } = payload;
      const res = await axios.patch(
        `/api/v1/vmeta/canvas/${canvasId}/data/file/${_id}`,
        body
      );
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "premium-dataset-file-successfully-edited",
        message: "Dataset File Successfully Updated",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["premiumDatasetFileList"],
      });
    },
    onError: error => {
      return error;
    },
  });
};

export const usePremiumDatasetFile = (): UseMutationResult<
  VmetaDataResponseBody,
  ErrorNotification,
  VmetaDataRequestParams
> => {
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<
    VmetaDataResponseBody,
    ErrorNotification,
    VmetaDataRequestParams
  >({
    mutationFn: async (params: VmetaDataRequestParams) => {
      const res = await axios.get(
        `/api/v1/vmeta/canvas/${params.canvasId}/data/file/${params.id}`
      );
      return res.data;
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "premium-dataset-file-fetch-failed",
        message: "Dataset File Fetch Failed",
        details:
          getErrorResponseMessage(error) ??
          "We were unable to upload your file",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useCreatePremiumDatasetFile = (): UseMutationResult<
  VmetaDataSetResponseBody,
  ErrorNotification,
  VmetaDataSetRequestParams & AddFilesVmetaDataSetRequestBody
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<
    VmetaDataSetResponseBody,
    ErrorNotification,
    VmetaDataSetRequestParams & AddFilesVmetaDataSetRequestBody
  >({
    mutationFn: async (
      payload: VmetaDataSetRequestParams & AddFilesVmetaDataSetRequestBody
    ): Promise<VmetaDataSetResponseBody> => {
      const { id, ...body } = payload;
      const res = await axios.post(`/api/v1/vmeta/dataset/${id}/addFile`, body);
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "premium-dataset-file-successfully-created",
        message: "Dataset File Successfully Created",
        details: "You can now add files to your dataset",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["premiumDatasetFileList"],
      });
    },
    onError: (error: ErrorNotification) => {
      return error;
    },
  });
};

export const useAddExistingFilesToDataset = (): UseMutationResult<
  VmetaDataSetResponseBody,
  ErrorNotification,
  VmetaDataSetRequestParams & AddExistingFilesVmetaDataSetRequestBody
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<
    VmetaDataSetResponseBody,
    ErrorNotification,
    VmetaDataSetRequestParams & AddExistingFilesVmetaDataSetRequestBody
  >({
    mutationFn: async (
      payload: VmetaDataSetRequestParams &
        AddExistingFilesVmetaDataSetRequestBody
    ): Promise<VmetaDataSetResponseBody> => {
      const { id, ...body } = payload;
      const res = await axios.post(
        `/api/v1/vmeta/dataset/${id}/addExistingFiles`,
        body
      );
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "premium-dataset-files-successfully-added",
        message: "Files Successfully Added to Dataset",
        details: "The selected files have been added to your dataset",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["premiumDatasetFileList"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "premium-dataset-files-add-failed",
        message: "Failed to Add Files to Dataset",
        details:
          error.response?.data?.message ??
          "We were unable to add the selected files to your dataset",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useDownloadDatasetTemplate = (): UseMutationResult<
  Blob,
  ErrorNotification,
  VmetaDataListParams
> => {
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<Blob, ErrorNotification, VmetaDataListParams>({
    mutationFn: async (payload: VmetaDataListParams): Promise<Blob> => {
      const { canvasId, collectionName } = payload;
      const res = await axios.post(
        `/api/v1/vmeta/canvas/${canvasId}/data/${collectionName}/fields/download?mode=insert`,
        undefined,
        {
          responseType: "blob",
        }
      );
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "premium-dataset-file-template-successfully-downloaded",
        message: "Dataset Template Successfully Downloaded",
        details: "You can now use this template to upload your dataset files",
        variant: NOTIFICATION.SUCCESS,
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "premium-dataset-file-template-download-failed",
        message: "Dataset Template Download Failed",
        details:
          getErrorResponseMessage(error) ??
          "We were unable to download the template",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useUploadSpreadsheet = (): UseMutationResult<
  {
    data: Array<Array<any>>;
  },
  ErrorNotification,
  File
> => {
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<
    {
      data: Array<Array<any>>;
    },
    ErrorNotification,
    File
  >({
    mutationFn: async (
      file: File
    ): Promise<{
      data: Array<Array<any>>;
    }> => {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(
        `/api/v1/vmeta/upload/spreadsheet`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "premium-dataset-file-successfully-uploaded",
        message: "Dataset File Successfully Uploaded",
        details: "You can now view your dataset files",
        variant: NOTIFICATION.SUCCESS,
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "premium-dataset-file-upload-failed",
        message: "Dataset File Upload Failed",
        details:
          getErrorResponseMessage(error) ??
          "We were unable to upload the template",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const fetchPremiumDataset = async (
  id: string
): Promise<VmetaDataSetResponseBody> => {
  try {
    const res = await axios.get(`/api/v1/vmeta/dataset/${id}`);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const usePremiumDataset = (
  id: string,
  enabled: boolean = true
): UseQueryResult<VmetaDataSetResponseBody, Error> => {
  return useQuery<VmetaDataSetResponseBody, Error>({
    queryKey: ["/api/v1/vmeta/dataset", id],
    queryFn: async () => await fetchPremiumDataset(id),
    retry: false,
    enabled: enabled && !!id,
  });
};

export const fetchProjectDatasetFields = async (
  params: VmetaDataListParams
): Promise<VmetaDataResponseBody> => {
  const res = await axios.get(
    `/api/v1/vmeta/canvas/${params.canvasId}/data/${params.collectionName}/fields?mode=insert`
  );
  return res.data;
};

export const useProjectDatasetFieldsQuery = (
  canvasId: string,
  collectionName: string,
  enabled: boolean = true
): UseQueryResult<VmetaDataResponseBody, ErrorNotification> => {
  return useQuery<VmetaDataResponseBody, ErrorNotification>({
    queryKey: [
      "/api/v1/vmeta/canvas/fields",
      canvasId,
      collectionName,
      "insert",
    ],
    queryFn: async () =>
      await fetchProjectDatasetFields({ canvasId, collectionName }),
    enabled: enabled && !!canvasId && !!collectionName,
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export const useGetProjectDatasetFields = (): UseMutationResult<
  VmetaDataResponseBody,
  ErrorNotification,
  VmetaDataListParams
> => {
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<
    VmetaDataResponseBody,
    ErrorNotification,
    VmetaDataListParams
  >({
    mutationFn: async (params: VmetaDataListParams) =>
      await fetchProjectDatasetFields(params),
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "project-dataset-fields-fetch-failed",
        message: "Project Dataset Fields Fetch Failed",
        details:
          getErrorResponseMessage(error) ??
          "We were unable to upload your file",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useGetFileCollectionId = (): UseMutationResult<
  string,
  ErrorNotification,
  string
> => {
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<string, ErrorNotification, string>({
    mutationFn: async (projectId: string) => {
      // First get all collections for the project
      const collections = await fetchVmetaCollections(
        100,
        0,
        [],
        [
          { id: "projectID", value: projectId },
          { id: "name", value: "file" },
        ]
      );

      if (collections.data && collections.data.length > 0) {
        return collections.data[0]._id;
      }

      throw new Error("File collection not found for project");
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "file-collection-id-fetch-failed",
        message: "Failed to Get Collection Information",
        details:
          error?.response?.data?.message ??
          "Could not retrieve collection information for field creation.",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

/**
 * Name and label of every field of a canvas's file collection (agent-5-5). The add flows use it to
 * tell whether "Source" and "Source Name" are the system source fields on this canvas.
 */
export const fetchFileCollectionFields = async (
  canvasId: string
): Promise<FileFieldNameLabel[]> => {
  const collections = await fetchVmetaCollections(
    100,
    0,
    [],
    [
      { id: "projectID", value: canvasId },
      { id: "name", value: "file" },
    ]
  );
  const fileCollection = (collections.data ?? []).find(
    collection => collection.name === "file"
  );
  if (!fileCollection) return [];
  const fields = await fetchVmetaFields(
    1000,
    0,
    [],
    [{ id: "collectionID", value: fileCollection._id }]
  );
  return (fields.data ?? []).map(field => ({
    name: field.name,
    label: field.label,
  }));
};

interface DownloadDatasetFilesRequestQuery {
  datasetId: string;
  datasetName?: string;
  filter?: { or: FilterItem[] };
  sort?: Array<{ id: string; desc: boolean }>;
}

export const useGenerateVmetaDownloadUrls = (): UseMutationResult<
  VmetaDownloadUrlsResponseBody,
  ErrorNotification,
  VmetaDownloadUrlsRequestBody
> => {
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<
    VmetaDownloadUrlsResponseBody,
    ErrorNotification,
    VmetaDownloadUrlsRequestBody
  >({
    mutationFn: async ({ files, downloadMode, datasetName, datasetId }) => {
      const res = await axios.post("/api/v1/vmeta/dataset/download-urls", {
        files,
        downloadMode,
        datasetName,
        datasetId,
      });
      return res.data;
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "generate-vmeta-download-urls-failed",
        message: "Failed to generate download URLs",
        details:
          error?.response?.data?.message ??
          "We couldn't generate download URLs for the files.",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useSendDatasetDownloadNotification = () => {
  return useMutation<void, ErrorNotification, { datasetName: string }>({
    mutationFn: async ({ datasetName }) => {
      await axios.post("/api/v1/vmeta/dataset/download-notification", {
        datasetName,
      });
    },
  });
};

export const useDownloadDatasetFiles = (): UseMutationResult<
  AxiosResponse<Blob>,
  ErrorNotification,
  DownloadDatasetFilesRequestQuery
> => {
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<
    AxiosResponse<Blob>,
    ErrorNotification,
    DownloadDatasetFilesRequestQuery
  >({
    mutationFn: ({ datasetId, filter, sort }) => {
      const baseUrl = `/api/v1/vmeta/dataset/${datasetId}/files/download`;
      const payload: any = {};

      // Add optional parameters if provided
      if (sort && sort.length > 0) {
        payload.sort = sort[0].id;
        payload.order = sort[0].desc ? "desc" : "asc";
      }

      // Handle filter using createMongoFilter (single source of truth)
      if (filter && filter.or.length > 0) {
        const mongoFilter = createMongoFilter(filter, {});
        payload.filter = mongoFilter;
      }

      return axios.post(baseUrl, payload, {
        responseType: "blob",
        withCredentials: true,
      });
    },
    onSuccess: (response, variables) => {
      try {
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const fileName = variables.datasetName
          ? `${variables.datasetName}.xlsx`
          : "dataset-files.xlsx";
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);

        setNotification({
          id: "dataset-files-download-success",
          message: "Files exported",
          details: "Dataset files are downloading.",
          variant: NOTIFICATION.SUCCESS,
        });
      } catch (error) {
        console.error("Error downloading file:", error);
        setNotification({
          id: "dataset-files-download-error",
          message: "Download failed",
          details: "Error occurred while downloading the file.",
          variant: NOTIFICATION.ERROR,
        });
      }
    },
    onError: error => {
      setNotification({
        id: "dataset-files-download-failed",
        message: "Export failed",
        details:
          error?.response?.data?.message ??
          "We couldn't export the dataset files.",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};
